import React, { Fragment } from "react";
import { withTranslation } from "react-i18next";
import { observer, inject } from "mobx-react";
import axios from "axios";
import {
  Tab,
  Button,
  Segment,
  Icon,
  Header,
  Divider,
  Feed,
  Form,
  Message
} from "semantic-ui-react";
import moment from "moment";

import ConfirmButton from "../controls/confirmButton";

class EditJobAttachments extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedFile: null,
      textField: "",
      loading: false,
      notSavedYet: false,
      saveMessage: false,
      dragVisible: false,

      jobIds: [],         // <-- NEW
      jobAttachments: []  // <-- NEW: store loaded attachments
    };

    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    this.resolveJobIds();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.edit.state.marked !== this.props.edit.state.marked) {
      this.resolveJobIds();
    }
  }

  /**
   * Collect jobIds from marked items
   */
  resolveJobIds = () => {
    let jobIds = [];
    let marked = this.props.edit.state.marked;

    for (const markedJob of marked) {
      let [process, date] = markedJob.split("#");

      const jobs = this.props.edit.state.data.processes[process].jobs;

      Object.keys(jobs).forEach((key) => {
        if (
          !jobs[key].deleted &&
          date === moment(jobs[key].start).format("YYYY-MM-DD")
        ) {
          jobIds.push(jobs[key].id);
        }
      });
    }

    this.setState({ jobIds }, () => this.loadJobAttachments());
  };

  /**
   * Load all attachments for the selected job IDs
   */
  loadJobAttachments = () => {
    if (!this.state.jobIds.length) return;

    axios
      .get(
        process.env.REACT_APP_SERVER_URL +
          "/getJobAttachments",
        {
          params: { jobIds: this.state.jobIds },
          headers: {
            Authorization: "Bearer " + localStorage.getItem("binfra_token"),
          },
        }
      )
      .then((response) => {
        this.setState({ jobAttachments: response.data });
      })
      .catch((error) => console.log(error));
  };

  /**
   * File select
   */
  fileChange = (e) => {
    const fname = e.target.value;
    this.setState({
      selectedFile: fname ? fname.replace(/.*[/\\]/, "") : null,
      saveMessage: false,
    });
  };

  allowDrag = (e) => {
    e.dataTransfer.dropEffect = "copy";
    e.preventDefault();
  };

  drop = (e) => {
    e.preventDefault();
    this.fileInputRef.current.files = e.dataTransfer.files;
    const fname = this.fileInputRef.current.value;

    this.setState({
      dragVisible: false,
      selectedFile: fname ? fname.replace(/.*[/\\]/, "") : null,
      saveMessage: false,
    });
  };

  changeTextField = (e, { value }) => {
    this.setState({ textField: value, saveMessage: false });
  };

  /**
   * Send attachment for multiple jobIds
   */
  send = () => {
    this.setState({ loading: true, saveMessage: false, notSavedYet: false });

    const formData = new FormData();
    const imagefile = this.fileInputRef.current;

    formData.append("file", imagefile.files.length ? imagefile.files[0] : false);
    formData.append("description", this.state.textField);
    formData.append("jobIds", JSON.stringify(this.state.jobIds)); // <--- IMPORTANT

    axios
      .post(
        process.env.REACT_APP_SERVER_URL + "/saveProjectJobAttachment",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: "Bearer " + localStorage.getItem("binfra_token"),
          },
        }
      )
      .then((response) => {
        this.setState({
          loading: false,
          selectedFile: null,
          textField: "",
          saveMessage: true,
        });

        this.loadJobAttachments();
      })
      .catch(() =>
        this.setState({ loading: false, notSavedYet: true, saveMessage: true })
      )
      .finally(() => {
        setTimeout(() => this.setState({ saveMessage: false }), 5000);
      });
  };

  /**
   * Delete attachment
   */
  deleteAttachment = (id) => {
    axios
      .delete(
        process.env.REACT_APP_SERVER_URL + "/deleteJobAttachment/" + id,
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("binfra_token"),
          },
        }
      )
      .then(() => {
        this.loadJobAttachments();
      });
  };

  /**
   * Render attachments list
   */
  renderAttachments = () => {
    if (!this.state.jobAttachments.length)
      return (
        <Segment placeholder size="small">
          <Header icon>
            <Icon name="times" />
            {this.props.t("edit.nodata.attachments.message")}
          </Header>
        </Segment>
      );

    return (
      <Feed size="large" className="messages">
        {this.state.jobAttachments.map((m) => (
          <Feed.Event key={m.id}>
            <Feed.Label icon="attach" />
            <Feed.Content>
              <Feed.Summary>
                <Feed.User>{m.creator}</Feed.User>{" "}
                added attachment
                <Feed.Date>{moment(m.creationTime).calendar()}</Feed.Date>
              </Feed.Summary>

              <Feed.Extra text>
                {m.description.length ? m.description : m.attachment}
              </Feed.Extra>

              <Feed.Meta>
                <Feed.Like
                  as="a"
                  target="_blank"
                  href={
                    process.env.REACT_APP_SERVER_URL +
                    "/getJobAttachment/" +
                    m.id 
                    // "/" +
                    // m.attachment
                  }
                >
                  <Icon name="attach" /> Open
                </Feed.Like>

                <ConfirmButton
                  onClick={() => this.deleteAttachment(m.id)}
                  trigger={
                    <Feed.Like>
                      <Icon name="trash alternate" /> Delete
                    </Feed.Like>
                  }
                />
              </Feed.Meta>
            </Feed.Content>
          </Feed.Event>
        ))}
      </Feed>
    );
  };

  render() {
    return (
      <Tab.Pane
        loading={this.state.loading}
        onDragEnter={() => this.setState({ dragVisible: true })}
      >
        {/* Upload section */}
        <Fragment>
          <Form>
            <Message
              visible={this.state.saveMessage}
              icon={this.state.notSavedYet ? "warning sign" : "checkmark"}
              error={this.state.notSavedYet}
              success={!this.state.notSavedYet}
              header={
                this.state.notSavedYet
                  ? this.props.t("report.log.message.warn.header")
                  : this.props.t("report.log.message.ok.header")
              }
              content={
                this.state.notSavedYet
                  ? this.props.t("report.log.message.warn.text")
                  : this.props.t("report.log.message.ok.text")
              }
            />

            <Form.TextArea
              label={this.props.t("report.log.textarea.title")}
              placeholder={this.props.t("report.log.textarea.placeholder")}
              value={this.state.textField}
              onChange={this.changeTextField}
            />

            <div className="flexgroup">
              <Button
                content={
                  this.state.selectedFile
                    ? this.state.selectedFile
                    : this.props.t("report.log.fileChoose")
                }
                positive={!!this.state.selectedFile}
                icon="attach"
                onClick={() => this.fileInputRef.current.click()}
              />

              <input
                type="file"
                ref={this.fileInputRef}
                hidden
                onChange={this.fileChange}
              />

              <Button
                content="Send"
                icon="send"
                primary
                onClick={this.send}
              />
            </div>
          </Form>

          <Divider />
        </Fragment>

        {/* List attachments */}
        {this.renderAttachments()}

        {/* Drag overlay */}
        <div
          style={{ display: this.state.dragVisible ? "flex" : "none" }}
          className="dropZone"
          onDragEnter={this.allowDrag}
          onDragOver={this.allowDrag}
          onDragLeave={() => this.setState({ dragVisible: false })}
          onDrop={this.drop}
        >
          <Icon name="attach" />
        </div>
      </Tab.Pane>
    );
  }
}

export default inject("app")(withTranslation()(observer(EditJobAttachments)));
